import { DataTableRow } from '../../../models/DataRequestHub/DataTable';

export interface UnsavedCellEdit {
    rowId: number;
    customColumnId: string;
    answerText: string;
}

// Rows confirmed by the save response replace their baseline counterparts, so change detection
// and the versions sent with the next save reflect the state the server actually holds.
export const mergeSavedRowsIntoBaseline = (
    baselineRows: DataTableRow[],
    savedRows: DataTableRow[]
): DataTableRow[] => {
    if (!savedRows?.length) {
        return baselineRows;
    }

    return baselineRows.map(
        (row) => savedRows.find((savedRow) => savedRow.id === row.id) ?? row
    );
};

// Grid rows keep whatever the user typed (they may already contain newer input); only the
// concurrency versions are refreshed so the next save is stamped against the confirmed state.
export const mergeSavedVersionsIntoGridRows = <T extends DataTableRow>(
    gridRows: T[],
    savedRows: DataTableRow[]
): T[] => {
    if (!savedRows?.length) {
        return gridRows;
    }

    return gridRows.map((gridRow) => {
        const savedRow = savedRows.find((saved) => saved.id === gridRow.id);
        if (!savedRow) {
            return gridRow;
        }

        return {
            ...gridRow,
            cells: gridRow.cells.map((cell) => {
                const savedCell = savedRow.cells.find(
                    (saved) => saved.id === cell.id
                );
                return savedCell
                    ? { ...cell, version: savedCell.version }
                    : cell;
            }),
        };
    });
};

// Cells whose flattened grid value differs from the last server-confirmed baseline — the user's
// not-yet-saved input. Grid rows carry the answer of each cell as a property keyed by the cell's
// customColumnId (see updateTableData in DataTableForm).
export const findUnsavedCellEdits = (
    gridRows: DataTableRow[],
    baselineRows: DataTableRow[]
): UnsavedCellEdit[] => {
    const unsavedEdits: UnsavedCellEdit[] = [];

    gridRows.forEach((gridRow) => {
        const baselineRow = baselineRows.find((row) => row.id === gridRow.id);
        if (!baselineRow) {
            return;
        }

        gridRow.cells.forEach((cell) => {
            const gridValue = (gridRow as any)[cell.customColumnId];
            if (gridValue === undefined || gridValue === null) {
                return;
            }

            const baselineCell = baselineRow.cells.find(
                (baseline) => baseline.customColumnId === cell.customColumnId
            );
            if (!baselineCell) {
                return;
            }

            if (gridValue !== (baselineCell.answerText ?? '')) {
                unsavedEdits.push({
                    rowId: gridRow.id,
                    customColumnId: cell.customColumnId,
                    answerText: gridValue,
                });
            }
        });
    });

    return unsavedEdits;
};

export const getBaselineAnswer = (
    baselineRows: DataTableRow[],
    rowId: number,
    customColumnId: string
): string | undefined =>
    baselineRows
        ?.find((row) => row.id === rowId)
        ?.cells.find((cell) => cell.customColumnId === customColumnId)
        ?.answerText;
